import { useState, useEffect } from 'react';

export const useDocumentResizing = (containerRef) => {
  const [isResizing, setIsResizing] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // Default 50% of available space
  const [tocWidth, setTocWidth] = useState(0);

  // Track TOC width changes
  useEffect(() => {
    if (!containerRef.current) return;

    const tocElement = containerRef.current.querySelector('.toc-container');
    if (!tocElement) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTocWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(tocElement);
    // Set initial width
    setTocWidth(tocElement.offsetWidth);

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        // Get TOC width
        const tocElement = containerRef.current.querySelector('.toc-container');
        const currentTocWidth = tocElement ? tocElement.offsetWidth : 0;

        // Calculate available width (container width minus TOC width)
        const availableWidth = containerRect.width - currentTocWidth;

        // Calculate mouse position relative to the available space (after TOC)
        const mouseX = e.clientX - containerRect.left - currentTocWidth;
        const newWidth = (mouseX / availableWidth) * 100;

        // Constrain the width (minimum 20%, maximum 80% of available space)
        const constrainedWidth = Math.max(20, Math.min(80, newWidth));
        setLeftPanelWidth(constrainedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, containerRef]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  return {
    isResizing,
    leftPanelWidth,
    rightPanelWidth: 100 - leftPanelWidth,
    tocWidth,
    handleResizeStart
  };
};